import { AdminPage } from "@/features/admin";
import { LoginPage, meQuery } from "@/features/auth";
import {
  CatalogDetailPage,
  CatalogIndexPane,
  CatalogsPage,
  DocumentReader,
} from "@/features/catalogs";
import { ProfilePage } from "@/features/profile";
import { SearchPage } from "@/features/search";
import type { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { AppShell } from "./shell";

interface RouterCtx {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterCtx>()({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: (s: Record<string, unknown>): { redirect?: string; error?: string } => ({
    ...(typeof s.redirect === "string" ? { redirect: s.redirect } : {}),
    ...(typeof s.error === "string" ? { error: s.error } : {}),
  }),
});

/** Authenticated layout: requires a session; redirects to /login. */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authed",
  component: AppShell,
  beforeLoad: async ({ context, location }) => {
    const user = await context.queryClient.ensureQueryData(meQuery);
    if (!user) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    return { user };
  },
});

const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/",
  component: () => (
    <div className="p-8">
      <h1 className="text-2xl font-semibold">Welcome</h1>
      <p className="mt-2 text-[var(--fg-muted)]">
        Pick a catalog from the sidebar, or use Search to look across everything you can read.
      </p>
    </div>
  ),
});

const catalogsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/catalogs",
  component: CatalogsPage,
});

const catalogDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/catalogs/$name",
  component: CatalogDetailPage,
});

const catalogDetailIndexRoute = createRoute({
  getParentRoute: () => catalogDetailRoute,
  path: "/",
  component: CatalogIndexPane,
});

const documentReaderRoute = createRoute({
  getParentRoute: () => catalogDetailRoute,
  path: "$",
  component: DocumentReader,
});

const searchRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/search",
  component: SearchPage,
});

const profileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/profile",
  component: ProfilePage,
});

const adminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/admin",
  component: AdminPage,
  beforeLoad: ({ context }) => {
    // authedRoute already guaranteed a user; re-fetch from cache synchronously.
    const user = context.queryClient.getQueryData(meQuery.queryKey) as { role: string } | undefined;
    if (!user || user.role !== "admin") throw redirect({ to: "/" });
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authedRoute.addChildren([
    indexRoute,
    catalogsRoute,
    catalogDetailRoute.addChildren([catalogDetailIndexRoute, documentReaderRoute]),
    searchRoute,
    profileRoute,
    adminRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}
